/**
 * Hook for managing WebSocket connection interval checking.
 * NOTE: Stream handler callbacks are managed by use-stream-callbacks.hook.ts in the chat view.
 */

import { useCallback, useEffect, useRef } from 'react'

import { isWebSocketConnected } from '../store/middleware/websocket.middleware'
import type { WebSocketConnectionStatus } from './agents-websocket.interface'

interface UseConnectionCheckParams {
  setConnectionStatus: (status: WebSocketConnectionStatus) => void
}

interface UseConnectionCheckReturn {
  startCheck: () => void
  clearCheck: () => void
}

export const useConnectionCheck = ({
  setConnectionStatus,
}: UseConnectionCheckParams): UseConnectionCheckReturn => {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const clearCheck = useCallback(() => {
    if (!intervalRef.current) return
    clearInterval(intervalRef.current)
    intervalRef.current = null
  }, [])

  const startCheck = useCallback(() => {
    clearCheck()
    intervalRef.current = setInterval(() => {
      setConnectionStatus(isWebSocketConnected() ? 'connected' : 'disconnected')
    }, 1000)
  }, [clearCheck, setConnectionStatus])

  useEffect(() => {
    return () => {
      clearCheck()
    }
  }, [clearCheck])

  return { startCheck, clearCheck }
}
